import { create } from '@bufbuild/protobuf'
import { useCallback, useMemo, useState } from 'react'

import {
  type IRQAnswer,
  IRQAnswerSchema,
  type InherentRiskGroup,
  type InherentRiskItem,
  InherentRiskQuestionType,
  InherentRiskSource,
} from '@/gen/inventory/v1/company_service_pb'
import { TemplateBuilderCondition } from '@/gen/inventory/v1/template_builder_lib_pb'

import { isConditionMet } from '@/components/shared/template-builder/lib/condition'

import { pickBestSingleSelectItem } from './utils'

type AnswerState = {
  selectedOptionIds: string[]
  content?: string
}

type Answers = Record<string, AnswerState>

type QuestionUpdate = {
  toggleOption?: string
  selectedOptionIds?: string[]
  content?: string
}

/** Minimal shape required by the store – satisfied by both InherentRiskGroup and InherentRiskTemplateQuestion. */
type IrqGroupInput = {
  id: string
  condition?: TemplateBuilderCondition
  inherentRiskItems?: InherentRiskItem[]
}

const uniqueOptionIds = (ids: string[]): string[] => [...new Set(ids)]

export function toIrqAnswers(answers: Answers, visibleGroupIds: Set<string>): IRQAnswer[] {
  return Object.entries(answers)
    .filter(([qId]) => visibleGroupIds.has(qId))
    .map(([questionId, s]) =>
      create(IRQAnswerSchema, {
        questionId,
        selectedOptionIds: s.selectedOptionIds,
        content: s.content,
      }),
    )
}

export function answersFromIrqGroups(groups: IrqGroupInput[]): Answers {
  return Object.fromEntries(
    groups
      .map((group) => {
        const irqItems = (group.inherentRiskItems ?? []).filter(
          // Only source IRQ items are considered for answers
          (item) => item.source === InherentRiskSource.IRQ,
        )
        if (irqItems.length === 0) return null
        return [
          group.id,
          {
            selectedOptionIds: uniqueOptionIds(
              irqItems.map((item) => item.id).filter(Boolean) as string[],
            ),
            content: irqItems.find((item) => item.content)?.content ?? undefined,
          },
        ]
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
  )
}

/**
 * Manages local IRQ answer state via useState.
 * Initializes from server data (IRQ-source items) and exposes mutation callbacks.
 */
export const useIrqAnswerStore = (serverGroups: IrqGroupInput[]) => {
  const [answers, setAnswers] = useState<Answers>(() => answersFromIrqGroups(serverGroups))

  const deleteQuestion = useCallback((questionId: string) => {
    setAnswers((prev) => {
      const { [questionId]: _, ...rest } = prev
      return rest
    })
  }, [])

  const updateQuestion = useCallback((questionId: string, update: QuestionUpdate) => {
    setAnswers((prev) => {
      let existing = prev[questionId] ?? { selectedOptionIds: [] }

      if (update.selectedOptionIds) {
        existing = { ...existing, selectedOptionIds: uniqueOptionIds(update.selectedOptionIds) }
      }

      if (update.toggleOption) {
        const isSelected = existing.selectedOptionIds.includes(update.toggleOption)
        existing = {
          ...existing,
          selectedOptionIds: isSelected
            ? existing.selectedOptionIds.filter((id) => id !== update.toggleOption)
            : [...existing.selectedOptionIds, update.toggleOption],
        }
      }

      if (update.content !== undefined) {
        if (update.content.trim().length === 0) {
          const { content: _, ...rest } = existing
          existing = rest as AnswerState
        } else {
          existing = { ...existing, content: update.content }
        }
      }

      if (existing.selectedOptionIds.length === 0 && !existing.content) {
        const { [questionId]: _, ...rest } = prev
        return rest
      }

      return { ...prev, [questionId]: existing }
    })
  }, [])

  const clearAll = useCallback(() => {
    setAnswers({})
  }, [])

  const removeSuggested = useCallback((lemaGroups: InherentRiskGroup[]) => {
    setAnswers((prev) => {
      const next = { ...prev }
      let changed = false
      for (const group of lemaGroups) {
        const existing = next[group.id]
        if (!existing) continue
        const removeIds = new Set(
          group.inherentRiskItems.map((i) => i.id).filter((id): id is string => !!id),
        )
        const removeContents = new Set(
          group.inherentRiskItems.map((i) => i.content).filter((c): c is string => !!c),
        )
        const selectedOptionIds = existing.selectedOptionIds.filter((id) => !removeIds.has(id))
        const content =
          existing.content && !removeContents.has(existing.content) ? existing.content : undefined
        changed = true
        if (selectedOptionIds.length === 0 && !content) {
          delete next[group.id]
        } else {
          next[group.id] = { selectedOptionIds, ...(content ? { content } : {}) }
        }
      }
      return changed ? next : prev
    })
  }, [])

  const fillSuggested = useCallback((lemaGroups: InherentRiskGroup[]) => {
    setAnswers((prev) =>
      lemaGroups.reduce((acc, group) => {
        const existing = acc[group.id] ?? { selectedOptionIds: [] }

        let items: InherentRiskItem[]
        if (group.type === InherentRiskQuestionType.SINGLE_SELECT) {
          if (existing.selectedOptionIds.length > 0) return acc
          const best = pickBestSingleSelectItem(group.inherentRiskItems)
          if (!best) return acc
          items = [best]
        } else {
          items = group.inherentRiskItems
        }

        const newIds = items
          .map((item) => item.id)
          .filter((id): id is string => !!id && !existing.selectedOptionIds.includes(id))
        const suggestedContent = existing.content ?? items.find((item) => item.content)?.content
        if (newIds.length === 0 && !suggestedContent) return acc
        return {
          ...acc,
          [group.id]: {
            ...existing,
            selectedOptionIds: uniqueOptionIds([...existing.selectedOptionIds, ...newIds]),
            ...(suggestedContent ? { content: suggestedContent } : {}),
          },
        }
      }, prev),
    )
  }, [])

  const visibleGroupIds = useMemo(
    () =>
      serverGroups.reduce((visible, { id, condition }) => {
        if (!condition) return visible.add(id)
        if (!visible.has(condition.questionId)) return visible
        const selectedIds = answers[condition.questionId]?.selectedOptionIds ?? []
        if (isConditionMet(condition, selectedIds)) visible.add(id)
        return visible
      }, new Set<string>()),
    [serverGroups, answers],
  )

  const selectedOptionIds = useMemo(
    () => Object.values(answers).flatMap((s) => s.selectedOptionIds),
    [answers],
  )

  return {
    answers,
    updateQuestion,
    deleteQuestion,
    clearAll,
    fillSuggested,
    removeSuggested,
    hasAnswers: Object.keys(answers).length > 0,
    selectedOptionIds,
    visibleGroupIds,
  }
}

export type IrqAnswerStore = ReturnType<typeof useIrqAnswerStore>
